/**
 * TutorialOfferPrompt
 *
 * A one-time, dismissible prompt that offers the guided tour to brand-new visitors.
 * Shows only on the composer route ("/" or "/compose"), never for shared/deep-link
 * entries, never if the tutorial was already completed, and only once ever
 * (localStorage flag). Accepting starts the existing tutorial.
 */
import { useEffect, useState } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import './TutorialOfferPrompt.css';

const OFFERED_KEY = '8bitcomposer_tutorial_offered';

export function TutorialOfferPrompt() {
  const { isActive, isCompleted, startTutorial } = useTutorial();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isCompleted) return;
    const { pathname, search } = window.location;
    const onComposer = pathname === '/' || pathname === '/compose';
    if (!onComposer || pathname.startsWith('/share/')) return;
    if (/template|share|remix/i.test(search)) return; // skip deep-link / remix entries
    try { if (localStorage.getItem(OFFERED_KEY)) return; } catch { /* ignore */ }
    const t = setTimeout(() => setShow(true), 1500); // let the page settle first
    return () => clearTimeout(t);
  }, [isCompleted]);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(OFFERED_KEY, '1'); } catch { /* ignore */ }
  };
  const accept = () => { dismiss(); startTutorial(); };

  if (!show || isActive || isCompleted) return null;

  return (
    <div className="tour-offer-scrim" onClick={dismiss}>
      <div
        className="tour-offer-card"
        role="dialog"
        aria-modal="true"
        aria-label="Take a guided tour"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="tour-offer-close" onClick={dismiss} aria-label="Dismiss tour offer">
          ✕
        </button>
        <div className="tour-offer-icon" aria-hidden="true">🎮</div>
        <h2 className="tour-offer-title">New here?</h2>
        <p className="tour-offer-subtitle">
          Take a quick 60-second tour and start making music in no time.
        </p>
        <div className="tour-offer-actions">
          <button className="tour-offer-btn tour-offer-btn-ghost" onClick={dismiss}>
            Maybe later
          </button>
          <button className="tour-offer-btn tour-offer-btn-primary" onClick={accept}>
            Start tour
          </button>
        </div>
      </div>
    </div>
  );
}
